import { FC, Fragment, useEffect, useState } from 'react';
import {
  FareFamilyDescription,
  FareFamilyInfo,
  FlightDetails,
} from 'ts/flight/interfaces/FlightDetailsResponse';
import { useTranslation } from 'react-i18next';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { faCheck, faDollarSign, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface FlexDetailsProps {
  flexFlights: FlightDetails[];
  onChooseFlex: (flightId: string) => void;
  flexFlightId: string;
}

const FlexDetails: FC<FlexDetailsProps> = (props) => {
  const [t] = useTranslation();
  const [description, setDescription] = useState<FareFamilyDescription[]>([]);
  const [info, setInfo] = useState<FareFamilyInfo[]>([]);
  const { i18n } = useTranslation();

  useEffect(() => {
    const newInfo = props.flexFlights
      .filter((flight) => {
        if (flight.inbound && flight.outbound && flight.fareFamilyInfo.length > 1) {
          return flight.fareFamilyInfo[0].desc === flight.fareFamilyInfo[1].desc;
        } else if (flight.outbound) {
          return flight.fareFamilyInfo;
        } else {
          return false;
        }
      })
      .map((flight) => {
        const priceObj = flight.flighPrice.grossAndTaxWithMarkup;
        return {
          ...flight.fareFamilyInfo[0],
          price: formatPriceCurrency(priceObj.value, getCurrency(priceObj.currency)),
          flightId: flight.id,
        };
      });
    if (newInfo.length) {
      const index = newInfo.findIndex((flight) => flight.flightId === props.flexFlightId);
      newInfo[index === -1 ? 0 : index].checked = true;
    }

    const descriptionList = newInfo.map((flight) => flight.fareFamilyDescription);
    const newDescription = descriptionList
      .flat()
      .filter((item, index, self) => {
        return index === self.findIndex((t) => t.desc === item.desc);
      })
      .filter((item) => item.sysCode > 1);
    setInfo(newInfo);
    setDescription(newDescription);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getFlexCell = (desc: string, column: FareFamilyInfo) => {
    const description = column.fareFamilyDescription.find((flight) => {
      return flight.desc === desc;
    });

    if (description?.code === 'CHA') {
      return {
        icon: faDollarSign,
        color: 'text-dark',
      };
    } else if (description?.code === 'INC') {
      return {
        icon: faCheck,
        color: 'text-success',
      };
    } else {
      return {
        icon: faMinus,
        color: 'danger-color',
      };
    }
  };

  const onClickFlex = (value: boolean, index: number) => {
    const newInfo = [...info];
    newInfo.forEach((item) => (item.checked = false));
    newInfo[index].checked = value;
    setInfo(newInfo);
    props.onChooseFlex(newInfo[index].flightId);
  };

  const getDesc = (desc: FareFamilyDescription) => {
    if (i18n.language === 'he') {
      return desc.descLang || desc.desc?.toLowerCase();
    } else {
      return desc.desc?.toLowerCase();
    }
  };

  return (
    <Fragment>
      {!!info.length && (
        <div className="default-shadow flight-details-item flight-details-flex">
          <div className="title-underline title-header-grey text-lg mb-20">
            {t('flight.updgrade')}
          </div>

          <div className="flex-table">
            <div
              className="flex-table-col names"
              style={{ flex: `0 0 ${100 / (info.length + 1)}%` }}
            >
              <div className="flex-table-cell">
                <div className="text-bold text-st">{t('hotel.description')}</div>
              </div>
              {description.map((row, rowIndex) => (
                <div className="flex-table-cell" key={rowIndex}>
                  {getDesc(row)}
                </div>
              ))}
              <div className="flex-table-cell"></div>
            </div>

            {info.map((column, columnIndex) => (
              <div
                className={'flex-table-col' + (column.checked ? ' checked' : '')}
                key={columnIndex}
                style={{ flex: `0 0 ${100 / (info.length + 1)}%` }}
              >
                <div className="flex-table-cell head text-bold">{column.desc}</div>
                {description.map((row, rowIndex) => (
                  <div className="flex-table-cell" key={rowIndex}>
                    <FontAwesomeIcon
                      className={getFlexCell(row.desc, column).color}
                      size="xl"
                      icon={getFlexCell(row.desc, column).icon as any}
                    />
                  </div>
                ))}
                <div className="flex-table-cell text-bold text-lg text-contrast">
                  <Checkbox
                    checked={column.checked}
                    className="mr-5"
                    onChange={(value) => onClickFlex(value, columnIndex)}
                  />
                  {column.price}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(FlexDetails, 'FlexDetails');
